import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

/**
 * Phone numbers are stored as E.164 (`+60123456789`). A field may hold several numbers, typed
 * like an email "To" box (separated by `;`, `,` or newlines) and stored joined by `"; "`, e.g.
 * `"+60102223333; +60169998888"`. The first number is the primary one (WhatsApp, SMS survey,
 * Firebase Auth). See docs/PHONE_NUMBER_FORMAT.md.
 *
 * Mirrored for server-side use by `server/utils/phone.ts`, and copied (subset) to
 * `apps/oms-public/app/utils/phone.ts`: change both together.
 */

export const PHONE_LIST_SEPARATOR = "; ";

const DEFAULT_COUNTRY: CountryCode = "MY";

// "/" only appears in legacy data ("0102… / 0169…"); new input is split the same way.
const LIST_SPLIT_PATTERN = /[;,\n\r/]+/;

/**
 * Normalizes one phone number to E.164, or returns null when it is not a valid number.
 * Handles the shapes found in existing data: legacy "+(60) 12 345 6789", local "012-345 6789",
 * "60123456789" without "+", a stray "+" before a local number ("+0123…") and a kept trunk
 * zero after the country code ("+60012…").
 */
export function normalizePhone(
  raw: string | null | undefined,
  defaultCountry: CountryCode = DEFAULT_COUNTRY,
): string | null {
  if (!raw) return null;

  const hasPlus = raw.trim().startsWith("+");
  let digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  if (hasPlus && digits.startsWith("0")) {
    // "+0123…" is a local number typed after a "+60" prefill was cleared.
    digits = digits.replace(/^0+/, "");
    return parseNational(digits, defaultCountry);
  }

  if (hasPlus) {
    // "+60 012…": trunk zero kept after the country code.
    if (digits.startsWith("600")) digits = `60${digits.slice(3)}`;
    const international = toE164(parsePhoneNumberFromString(`+${digits}`));
    if (international) return international;

    // The legacy app used a fixed "+(6_) __ ___ ____" mask, so the typed local digits start after
    // the "6": "+(61) 80…" was "180…" (toll-free), "+(63) 36…" was "336…".
    if (/^\+\s*\(6/.test(raw.trim()))
      return parseNational(digits.slice(1), defaultCountry);

    return null;
  }

  return parseNational(digits, defaultCountry);
}

function parseNational(digits: string, country: CountryCode): string | null {
  const asNational = toE164(parsePhoneNumberFromString(digits, country));
  if (asNational) return asNational;

  // "60123456789" (country code without "+").
  return toE164(parsePhoneNumberFromString(`+${digits}`));
}

function toE164(
  phone: ReturnType<typeof parsePhoneNumberFromString>,
): string | null {
  return phone?.isValid() ? phone.number : null;
}

/** Splits a typed or stored list, normalizes each entry and drops duplicates. */
export function parsePhoneList(input: string | null | undefined): {
  phones: string[];
  invalid: string[];
} {
  const phones: string[] = [];
  const invalid: string[] = [];

  for (const rawEntry of (input ?? "").split(LIST_SPLIT_PATTERN)) {
    const entry = rawEntry.trim();
    if (!entry) continue;

    const phone = normalizePhone(entry);
    if (!phone) {
      invalid.push(entry);
      continue;
    }

    if (!phones.includes(phone)) {
      phones.push(phone);
    }
  }

  return { phones, invalid };
}

export function formatPhoneList(phones: string[] | null | undefined): string {
  return (phones ?? []).join(PHONE_LIST_SEPARATOR);
}

/** Canonical stored form of a phone field, or "" when nothing in it parses. */
export function normalizePhoneList(input: string | null | undefined): string {
  return formatPhoneList(parsePhoneList(input).phones);
}

export function primaryPhone(value: string | null | undefined): string | null {
  return parsePhoneList(value).phones[0] ?? null;
}

/** Digits of the primary number without "+", as wa.me and the SMS gateway expect. */
export function phoneDigits(value: string | null | undefined): string {
  return primaryPhone(value)?.slice(1) ?? "";
}

/** Human-readable list, e.g. "+60 12-345 6789; +60 16-999 8888". Falls back to the raw value. */
export function formatPhoneForDisplay(
  value: string | null | undefined,
): string {
  if (!value) return "";

  const { phones, invalid } = parsePhoneList(value);
  if (!phones.length || invalid.length) return value;

  return phones
    .map(
      (phone) =>
        parsePhoneNumberFromString(phone)?.formatInternational() ?? phone,
    )
    .join(PHONE_LIST_SEPARATOR);
}
